import Konva from "konva";
import { Stage } from "konva/lib/Stage";
import {
  DragEvent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Group, Layer } from "react-konva";
import {
  addRectangle,
  deleteRectanglesBulk,
  groupRectangles,
} from "@store/slices/workspaceSlice";
import {
  clearRectSelection,
  setRectSelection,
} from "@features/annogram/canvas/state/canvasAuxSlice";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  selectIsGradingViewActive,
  selectViewedGroups,
  selectViewedHighlights,
  selectViewedRectangles,
} from "@store/selectors";
import {
  autoLabel,
  collapsedDisplayBody,
  collapsedRectSize,
} from "@shared/utils/utils";
import ArrowContextMenu from "./ArrowContextMenu";
import CanvasDiagnosticLayer from "./CanvasDiagnosticLayer";
import CanvasEvaluationToolbar from "./CanvasEvaluationToolbar";
import CanvasGhostLayer from "./CanvasGhostLayer";
import CanvasGroupFrame from "./CanvasGroupFrame";
import CanvasRectangle from "./CanvasRectangle";
import GroupContextMenu from "./GroupContextMenu";
import CanvasSelectionLayer from "./CanvasSelectionLayer";
import OffscreenIndicator from "./OffscreenIndicator";
import GroupEdgeLayer from "./GroupEdgeLayer";
import GroupEdgeContextMenu from "./GroupEdgeContextMenu";
import CanvasStage from "./CanvasStage";
import LabelRectModal from "./LabelRectModal";
import RectangleContextMenu from "./RectangleContextMenu";
import type { RectangleGeometry } from "@/types/canvasTypes";
import { TOUR } from "@features/onboarding/tourTargets";

interface Props {
  draggedHighlightId: string;
  clearDraggedHighlightId: () => void;
  canvasOn: boolean;
  stageRef: RefObject<Konva.Stage>;
}

const newGroupId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `g-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Default name for a new group: the first unused letter among existing
 * auto-names ("Group A", "Group B", ...), so groups are tellable apart
 * in edge menus and frame labels without the user having to rename.
 * User renames overwrite this like any name. Falls back to a numbered
 * name if all 26 letters are somehow in use.
 */
const nextGroupName = (existing: ReadonlyArray<{ name?: string }>): string => {
  const used = new Set(
    existing
      .map((g) => /^Group ([A-Z])$/.exec(g.name?.trim() ?? "")?.[1])
      .filter((letter): letter is string => letter !== undefined)
  );
  for (let i = 0; i < 26; i++) {
    const letter = String.fromCharCode(65 + i);
    if (!used.has(letter)) return `Group ${letter}`;
  }
  return `Group ${existing.length + 1}`;
};

const Canvas = ({
  draggedHighlightId,
  clearDraggedHighlightId,
  canvasOn,
  stageRef,
}: Props) => {
  const dispatch = useAppDispatch();
  const highlights = useAppSelector(selectViewedHighlights);
  const rectangles = useAppSelector(selectViewedRectangles);
  const groups = useAppSelector(selectViewedGroups);
  const isGradingView = useAppSelector(selectIsGradingViewActive);

  // selectionBox is the transient drag-to-select rectangle (canvas-
  // local coords). The captured selection itself lives in canvasAux
  // so CanvasRectangle (and other consumers) can read it via selector
  // without prop drilling.
  const selectedRectIndices = useAppSelector(
    (s) => s.canvasAux.selectedRectIndices
  );
  const [selectionBox, setSelectionBox] = useState<RectangleGeometry | null>(
    null
  );
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);

  // Stage 6.9: pending drop awaiting a label from LabelRectModal. We
  // hold the resolved highlight + drop point here so the modal's
  // submit/cancel handlers can dispatch addRectangle with the user's
  // chosen tag (or the auto-derived one) without re-resolving state
  // that may have changed underneath them (highlight list, pointer).
  const [pendingDrop, setPendingDrop] = useState<{
    x: number;
    y: number;
    text?: string;
    image?: string;
    category: string;
    fallbackWidth: number;
    fallbackHeight: number;
    sourceAnnotationClientId: string;
    sourcePageNumber?: number;
    defaultLabel: string;
  } | null>(null);

  const clearSelection = useCallback(() => {
    dispatch(clearRectSelection());
    setSelectionBox(null);
    dragStartRef.current = null;
  }, [dispatch]);

  // -----------------------------------------------------------------
  // Box-select wiring
  //
  // Shift+drag on empty Stage starts a selection box. Plain drag on
  // empty Stage still pans (existing behavior). Holding shift toggles
  // the Stage's draggable flag so Konva doesn't kick off a pan when
  // the user means to box-select.
  // -----------------------------------------------------------------

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    if (isGradingView) return;

    const onMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
      // Only treat as box-select when shift is held AND the user
      // pressed on the stage background, not a rect or frame.
      if (!e.evt.shiftKey) return;
      if (e.target !== stage) return;
      // Suppress the would-be stage pan for the duration of this drag.
      stage.draggable(false);
      const pos = stage.getRelativePointerPosition();
      if (!pos) return;
      dragStartRef.current = { x: pos.x, y: pos.y };
      setSelectionBox({ x: pos.x, y: pos.y, width: 0, height: 0 });
    };

    const onMouseMove = () => {
      const start = dragStartRef.current;
      if (!start) return;
      const pos = stage.getRelativePointerPosition();
      if (!pos) return;
      setSelectionBox({
        x: Math.min(start.x, pos.x),
        y: Math.min(start.y, pos.y),
        width: Math.abs(pos.x - start.x),
        height: Math.abs(pos.y - start.y),
      });
    };

    const onMouseUp = () => {
      const box = selectionBox;
      const start = dragStartRef.current;
      // Restore the stage's normal pan behavior.
      stage.draggable(true);
      dragStartRef.current = null;
      if (!start || !box) return;
      setSelectionBox(null);
      // Ignore micro-drags — treat them as a stray click.
      if (box.width < 3 && box.height < 3) return;
      const hits: number[] = [];
      rectangles.forEach((rect, idx) => {
        const intersects =
          rect.x < box.x + box.width &&
          rect.x + rect.width > box.x &&
          rect.y < box.y + box.height &&
          rect.y + rect.height > box.y;
        if (intersects) hits.push(idx);
      });

      // Auto-group when the drag captures 2+ free rects. If any of the
      // hits are already in a different group, we'd just be silently
      // refused at the reducer level — fall back to plain selection so
      // the user can resolve manually (ungroup first via right-click,
      // then re-shift-drag).
      const alreadyGroupedIndices = new Set<number>();
      for (const g of groups) {
        for (const i of g.memberRectIndices) alreadyGroupedIndices.add(i);
      }
      const hasConflict = hits.some((i) => alreadyGroupedIndices.has(i));
      if (hits.length >= 2 && !hasConflict) {
        // Skip the intermediate selection state — go straight to a
        // persistent group so the user sees the frame immediately.
        dispatch(clearRectSelection());
        dispatch(
          groupRectangles({
            id: newGroupId(),
            memberRectIndices: hits,
            name: nextGroupName(groups),
          })
        );
      } else {
        dispatch(setRectSelection(hits));
      }
    };

    stage.on("mousedown.canvas-select", onMouseDown);
    stage.on("mousemove.canvas-select", onMouseMove);
    stage.on("mouseup.canvas-select", onMouseUp);
    return () => {
      stage.off("mousedown.canvas-select");
      stage.off("mousemove.canvas-select");
      stage.off("mouseup.canvas-select");
    };
  }, [stageRef, rectangles, groups, selectionBox, isGradingView, dispatch]);

  // -----------------------------------------------------------------
  // Keyboard: Esc clears selection, Delete/Backspace bulk-deletes.
  // Cmd+G / Cmd+Shift+G removed — shift+drag now auto-forms a group,
  // and ungrouping is via the frame's right-click menu. We keep the
  // selection-clear and bulk-delete keybinds because they still apply
  // to selections that come from Cmd-click or shift+drag with a
  // conflicting target (where auto-group falls back to plain select).
  // -----------------------------------------------------------------

  useEffect(() => {
    if (isGradingView) return;
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack the user's keystrokes when they're typing into a
      // textarea / input (rectangle edit modal, group rename, etc.).
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) {
        return;
      }

      if (e.key === "Escape" && selectedRectIndices.length > 0) {
        clearSelection();
        return;
      }

      // Bulk delete of the current selection. Backspace too because
      // mac users frequently delete with that key in canvas-like apps.
      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        selectedRectIndices.length > 0 &&
        !e.metaKey &&
        !e.ctrlKey
      ) {
        e.preventDefault();
        if (
          !window.confirm(
            `Delete ${selectedRectIndices.length} selected rectangle${
              selectedRectIndices.length === 1 ? "" : "s"
            }? This can't be undone.`
          )
        ) {
          return;
        }
        dispatch(deleteRectanglesBulk({ indices: selectedRectIndices }));
        clearSelection();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isGradingView, selectedRectIndices, dispatch, clearSelection]);

  function handleDrop(
    event: DragEvent<HTMLDivElement>,
    stage: Stage | null,
    draggedHighlightId: string
  ) {
    event.preventDefault();

    // Stage 6.8: owner in grading view shouldn't mutate their own
    // canvas via drag-to-canvas while viewing a contributor's work.
    if (isGradingView) return;

    if (!draggedHighlightId || !stage) return;

    const highlight = highlights.find(
      (highlight) => highlight.id === draggedHighlightId
    );

    if (!highlight) return;

    stage.setPointersPositions(event);

    // Relative position: accounts for stage pan/zoom so the dropped
    // node lands under the pointer, not at a screen-space offset.
    const pointerPosition = stage.getRelativePointerPosition();

    if (!pointerPosition) return;

    const { text, image } = highlight.content;
    const { category } = highlight.comment;
    const { x1, x2, y1, y2 } = highlight.position.boundingRect;
    const { x, y } = pointerPosition;

    // Image-only highlights skip the label prompt — there's no text to
    // auto-label and no semantic value in a separate caption. Drop them
    // straight in as before.
    if (!text) {
      dispatch(
        addRectangle({
          x,
          y,
          category,
          text,
          image,
          expanded: false,
          tag: "",
          width: x2 - x1,
          height: y2 - y1,
          arrows: [],
          source_annotation_client_id: highlight.id,
          source_page_number: highlight.position.pageNumber,
        })
      );
      clearDraggedHighlightId();
      return;
    }

    setPendingDrop({
      x,
      y,
      text,
      image,
      category,
      fallbackWidth: x2 - x1,
      fallbackHeight: y2 - y1,
      sourceAnnotationClientId: highlight.id,
      sourcePageNumber: highlight.position.pageNumber,
      defaultLabel: autoLabel(text),
    });
    // Release the dragged-highlight handle now; the modal owns the rest
    // of the flow so a quick second drag isn't blocked while it's open.
    clearDraggedHighlightId();
  }

  const commitPendingDrop = (label: string) => {
    if (!pendingDrop) return;
    const tag = label.trim() || pendingDrop.defaultLabel;
    const sized = pendingDrop.text
      ? collapsedRectSize(
          collapsedDisplayBody(tag, pendingDrop.text),
          pendingDrop.sourcePageNumber !== undefined
        )
      : { width: pendingDrop.fallbackWidth, height: pendingDrop.fallbackHeight };
    dispatch(
      addRectangle({
        x: pendingDrop.x,
        y: pendingDrop.y,
        category: pendingDrop.category,
        text: pendingDrop.text,
        image: pendingDrop.image,
        expanded: false,
        tag,
        ...sized,
        arrows: [],
        // Stage 6.0: preserve provenance so the canvas-grading util can
        // pair student rectangles with expert rectangles by source.
        // Multiple drags of the same highlight produce multiple rects
        // with the same id — intentional (multi-instance use case).
        source_annotation_client_id: pendingDrop.sourceAnnotationClientId,
        source_page_number: pendingDrop.sourcePageNumber,
      })
    );
    setPendingDrop(null);
  };

  return (
    <div
      data-tour={TOUR.canvasSurface}
      className={`argument-canvas-container ${
        !canvasOn ? "argument-canvas-container--hidden" : ""
      }`}
      onDrop={(e) => handleDrop(e, stageRef.current, draggedHighlightId)}
      onDragOver={(e) => e.preventDefault()}
    >
      <CanvasEvaluationToolbar />
      <OffscreenIndicator />
      <CanvasStage stageRef={stageRef}>
        {/* Each Konva Layer is its own full-size canvas element, so
            they're kept to four (Konva recommends 3-5): z-ordering
            within a layer is done with Groups instead. */}
        {/* Group frames render first so they sit visually behind
            everything; edges draw over the frame fills. Both track
            group geometry, so they co-redraw anyway. Frames stay
            interactive — frame drag moves all member rects. */}
        <Layer name="group-layer">
          <CanvasGroupFrame />
          <GroupEdgeLayer />
        </Layer>
        <Layer name="arrow-layer" />
        <Layer>
          {/* Stage 6.5: faded dashed-outline expert rects the student
              missed, rendered behind the live student rects via
              listening:false so they don't intercept events. */}
          <CanvasGhostLayer />
          {rectangles.map((_, idx) => (
            <CanvasRectangle key={"rectangle" + idx} idx={idx} />
          ))}
          <CanvasDiagnosticLayer />
        </Layer>
        {/* One overlay layer for both transient surfaces: the marquee
            (below) and the drag portal (above). They're mutually
            exclusive interactions, and Portal targets resolve named
            Groups the same as named Layers. */}
        <Layer name="overlay-layer">
          <Group listening={false}>
            <CanvasSelectionLayer box={selectionBox} />
          </Group>
          <Group name="dragging-layer" />
        </Layer>
      </CanvasStage>
      <ArrowContextMenu />
      <RectangleContextMenu />
      <GroupContextMenu />
      <GroupEdgeContextMenu />
      <LabelRectModal
        open={pendingDrop !== null}
        defaultLabel={pendingDrop?.defaultLabel ?? ""}
        onSubmit={commitPendingDrop}
        onCancel={() => commitPendingDrop(pendingDrop?.defaultLabel ?? "")}
      />
    </div>
  );
};

export default Canvas;
